import { WordResponseType } from "@store/assessmentSlice";
import {
    AssessmentResult,
    AssessmentResultsCode,
    AssessmentScores,
    ResultSection,
    ScoreResult,
    ScoringCategories
} from "@store/generalTypes";
import { translate } from "react-i18nify";

const getResultFromScore = (
    score: number | undefined,
    normal: number,
    impaired: number,
    lessIsBetter: boolean
): AssessmentResultsCode => {
    if (score === undefined) return AssessmentResultsCode.Discontinued;
    if ((score >= normal && !lessIsBetter) || (score <= normal && lessIsBetter)) {
        return AssessmentResultsCode.Normal;
    }
    if ((score <= impaired && !lessIsBetter) || (score >= impaired && lessIsBetter)) {
        return AssessmentResultsCode.Impaired;
    }
    return AssessmentResultsCode.Borderline;
};

export const convertApiScoreToAssessmentScore = (apiScore: ScoreResult): AssessmentScores => {
    const ret = {} as AssessmentScores;

    if (!apiScore || !apiScore.trialScores || !apiScore.pieRatio || !apiScore.totalLearningScore) return ret;

    if (apiScore.trialScores.some((r) => r.name === ScoringCategories.CuedRecallB1)) {
        const score = apiScore.trialScores.find((r) => r.name === ScoringCategories.CuedRecallB1)?.score ?? 0;
        ret.b1CuedRecall = {
            score,
            result: getResultFromScore(score, 4, 2, false)
        };
    }

    if (apiScore.trialScores.some((r) => r.name === ScoringCategories.B1Intrusions)) {
        const score = apiScore.trialScores.find((r) => r.name === ScoringCategories.B1Intrusions)?.score ?? 0;
        ret.b1Intrusions = {
            score,
            result: getResultFromScore(score, 5, 7, true)
        };
    }

    if (apiScore.trialScores.some((r) => r.name === ScoringCategories.CuedRecallB2)) {
        const score = apiScore.trialScores.find((r) => r.name === ScoringCategories.CuedRecallB2)?.score ?? 0;
        ret.b2CuedRecall = {
            score,
            result: getResultFromScore(score, 7, 5, false)
        };
    }

    if (apiScore.trialScores.some((r) => r.name === ScoringCategories.CuedB2Intrusions)) {
        const score = apiScore.trialScores.find((r) => r.name === ScoringCategories.CuedB2Intrusions)?.score ?? 0;
        ret.b2CuedIntrusions = {
            score,
            result: getResultFromScore(score, 4, 6, true)
        };
    }

    if (apiScore.pieRatio) {
        ret.pieRatio = {
            score: apiScore.pieRatio.score,
            result: getResultFromScore(apiScore.pieRatio.score, 0.39, 0.4, true)
        };
    }

    ret.totalLearning = {
        score: apiScore.totalLearningScore.score,
        result: getResultFromScore(apiScore.totalLearningScore.score, 11, 10, false)
    };

    return ret;
};

export const getTableRowsFromAssessmentScores = (score: AssessmentScores): AssessmentScoreRow[] => {
    const ret: AssessmentScoreRow[] = [];
    if (!score) return ret;

    // Cued Recall B1
    ret.push({
        category: translate("admin.cuedRecall") + " B1",
        score: score.b1CuedRecall?.score,
        normal: 4,
        marginal: 3,
        impaired: 2,
        lowerIsBetter: false,
        result: score.b1CuedRecall?.result
    } as AssessmentScoreRow);

    // B1 Intrusions
    ret.push({
        category: translate("admin.b1Intrusions"),
        score: score.b1Intrusions?.score,
        normal: 5,
        marginal: 6,
        impaired: 7,
        lowerIsBetter: true,
        result: score.b1Intrusions?.result
    } as AssessmentScoreRow);

    // Cued Recall B2
    ret.push({
        category: translate("admin.cuedRecall") + " B2",
        score: score.b2CuedRecall?.score,
        normal: 7,
        marginal: 6,
        impaired: 5,
        lowerIsBetter: false,
        result: score.b2CuedRecall?.result
    } as AssessmentScoreRow);

    // Cued B2 Intrusions
    ret.push({
        category: translate("admin.cuedIntrusions", { bValue: "B2" }),
        score: score.b2CuedIntrusions?.score,
        normal: 4,
        marginal: 5,
        impaired: 6,
        lowerIsBetter: true,
        result: score.b2CuedIntrusions?.result
    } as AssessmentScoreRow);

    // Cued B1 Intrusions; PIE Ratio
    ret.push({
        category: translate("admin.cuedIntrusions", { bValue: "B1" }) + ": " + translate("admin.pieRatio"),
        score: score.pieRatio?.score,
        normal: 0.39,
        impaired: 0.4,
        lowerIsBetter: true,
        result: score.pieRatio?.result
    } as AssessmentScoreRow);

    return ret;
};

export const getTableRowsFromTotalLearning = (score: AssessmentScores): AssessmentScoreRow[] => {
    const ret: AssessmentScoreRow[] = [];
    if (!score) return ret;

    // Total Learning
    ret.push({
        category: translate("admin.totalLearning"),
        score: score.totalLearning?.score,
        normal: 11,
        impaired: 10,
        lowerIsBetter: false,
        result: score.totalLearning?.result
    } as AssessmentScoreRow);

    return ret;
};

export interface AssessmentScoreRow extends TableData {
    category: string;
    score?: number;
    normal: number;
    marginal?: number;
    impaired: number;
    result: AssessmentResultsCode;
    lowerIsBetter: boolean;
}

interface TableData {
    cellClasses: string;
}

export const getMoreOrLessText = (more: boolean): string => {
    return more ? translate("admin.orMore") : translate("admin.orLess");
};

export const getClassFromResultsCode = (code: AssessmentResultsCode): string => {
    switch (code) {
        case AssessmentResultsCode.Discontinued:
        case AssessmentResultsCode.Impaired:
            return "has-text-danger";
        case AssessmentResultsCode.Borderline:
            return "has-text-orange";
        default:
            return "has-text-green";
    }
};

export const getUIResultsFromAPIResults = (result: AssessmentResult): ResultSection[] => {
    const ret: ResultSection[] = [];
    result.responses.forEach((resp) => {
        if (trialIsRecall(resp.trial)) {
            if (ret && ret.length > 0) {
                const lastRecord = ret.at(-1)!;
                const numIndex = getIndexOfNumberInTrial(resp.trial);
                // The last record added is cued and has the same "prefix"
                // as the last added record, add it to that record instead of creating new.
                if (
                    lastRecord.isCued &&
                    lastRecord.title.substring(0, numIndex) === resp.trial.substring(0, numIndex)
                ) {
                    lastRecord.result.push(resp);
                    lastRecord.totalCorrect =
                        lastRecord.totalCorrect +
                        resp.responses.filter((r) => r.responseType === WordResponseType.Correct).length;
                    lastRecord.totalIncorrect =
                        lastRecord.totalIncorrect +
                        resp.responses.filter((r) => r.responseType !== WordResponseType.Correct).length;
                    lastRecord.totalWords = lastRecord.totalWords + resp.responses.length;
                } else {
                    ret.push({
                        isCued: trialIsCued(resp.trial),
                        isRecall: true,
                        title: resp.trial.substring(0, numIndex + 1),
                        result: [resp],
                        totalCorrect: resp.responses.filter((r) => r.responseType === WordResponseType.Correct).length,
                        totalIncorrect: resp.responses.filter((r) => r.responseType !== WordResponseType.Correct)
                            .length,
                        totalWords: resp.responses.length
                    });
                }
            }
        } else {
            ret.push({
                isCued: false,
                isRecall: false,
                title: resp.trial,
                result: [resp],
                totalCorrect: resp.responses.filter((r) => r.responseType === WordResponseType.Correct).length,
                totalIncorrect: resp.responses.filter((r) => r.responseType !== WordResponseType.Correct).length,
                totalWords: resp.responses.length
            });
        }
    });
    return ret;
};

const getIndexOfNumberInTrial = (trial: string): number => {
    const reg = /\d/;
    const num = trial.match(reg);
    if (!num || num.length > 1) return -1;
    return trial.indexOf(num[0]);
};

export const trialIsRecall = (trial: string): boolean => {
    return (
        trial.toLowerCase().startsWith("list a cued") ||
        trial.toLowerCase().startsWith("list b cued") ||
        trial.toLowerCase().startsWith("list a free") ||
        trial.toLowerCase().startsWith("list b free")
    );
};

const trialIsCued = (trial: string): boolean => {
    return trial.toLowerCase().includes("cued");
};

export const getCategoryFromCuedTitle = (trial: string): string => {
    return trial.split(" - ")[1];
};
